import useAllCurrencies from "@/services/global-services/query/useAllCurrencies";
import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";

// Création du contexte pour stocker les données génériques de l'app
const AppDataContext = createContext();

// Constantes
const RETRY_INTERVAL_MS = 10000; // 10 secondes

export const AppDataProvider = ({ children }) => {
    const {
        currencies = [],
        loading: loadingCurrencies,
        error: errorCurrencies,
        refetch
    } = useAllCurrencies();

    const intervalRef = useRef(null);

    // Mémorise les données de l'app pour éviter les re-rendus inutiles
    const appDatas = useMemo(() => ({
        currencies,
        loadingCurrencies,
        errorCurrencies
    }), [currencies, loadingCurrencies, errorCurrencies]);

    // Gestion du retry automatique en cas d'erreur
    useEffect(() => {
        // Nettoie l'intervalle existant
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }

        // Cleanup à la destruction du composant
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [errorCurrencies, refetch]);

    return (
        <AppDataContext.Provider value={appDatas}>
            {children}
        </AppDataContext.Provider>
    );
};

/**
 * Hook pour consommer le contexte AppData dans n'importe quel composant
 * @returns {Object} Les données de l'application (currencies, loadingCurrencies, errorCurrencies)
 * @throws {Error} Si utilisé en dehors du AppDataProvider
 *
 * @example
 * import { useAppData } from "@/hooks/global-hooks/AppDataContext";
 *
 * const MyComponent = () => {
 *   const { currencies, loadingCurrencies, errorCurrencies } = useAppData();
 *
 *   if (loadingCurrencies) return <span className="loader" />;
 *   if (errorCurrencies) return <PrintErrorMessage error={errorCurrencies} />;
 *
 *   return (
 *     <div>
 *       {currencies.map(currency => (
 *         <div key={currency.id}>{currency.name}</div>
 *       ))}
 *     </div>
 *   );
 * };
 */
export const useAppData = () => {
    const context = useContext(AppDataContext);

    if (context === undefined) {
        throw new Error("useAppData must be used within an AppDataProvider");
    }

    return context;
};
